import { Auth, getIdToken } from '@angular/fire/auth';
import { environment } from '@env/environment';
import { HttpParams } from '@angular/common/http';

export type ApiError = {
  status: number;
  message: string;
};

export type FetchOptions = RequestInit & {
  params?: Record<string, string> | HttpParams;
  requiresAuth?: boolean;
};

const createApiError = (status: number, message: string): ApiError => ({
  status,
  message,
});

const getAuthHeaders = async (auth: Auth): Promise<HeadersInit> => {
  const user = auth.currentUser;
  if (!user) return {};

  const token = await getIdToken(user, true);
  return {
    Authorization: `Bearer ${token}`,
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    // Try to parse JSON error; fall back to status text
    let message = response.statusText || `HTTP error! status: ${response.status}`;
    try {
      const errJson = await response.json();
      if (errJson && errJson.message) message = errJson.message;
    } catch (_) {
      // ignore parse errors
    }
    throw createApiError(response.status, message);
  }

  // No Content
  if (response.status === 204) {
    return null as T;
  }

  const contentType = response.headers.get('content-type') || '';

  // Read body once to guard against empty responses
  const raw = await response.text();
  if (!raw || raw.trim().length === 0) {
    // Treat empty body as null to avoid JSON parse errors
    return null as T;
  }

  if (contentType.includes('text/plain')) {
    return raw as unknown as T;
  }

  // Default to JSON parsing
  try {
    return JSON.parse(raw) as T;
  } catch (e) {
    // If parsing fails, return raw as fallback
    return raw as unknown as T;
  }
};

const createApiFetch = (auth: Auth) => {
  return async <T>(
    endpoint: string,
    options: FetchOptions = {},
  ): Promise<T> => {
    const { params, requiresAuth = true, ...init } = options;

    const url = new URL(`${environment.apiv3Url}${endpoint}`);
    if (params) {
      if (params instanceof HttpParams) {
        params.keys().forEach((key) => {
          url.searchParams.append(key, params.get(key)!);
        });
      } else {
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.append(key, value);
        });
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(requiresAuth ? await getAuthHeaders(auth) : {}),
      ...init.headers,
    };

    const response = await fetch(url.toString(), {
      ...init,
      headers,
    });
    return handleResponse<T>(response);
  };
};

export const createApi = (auth: Auth) => {
  const apiFetch = createApiFetch(auth);

  return {
    get: <T>(
      endpoint: string,
      options?: Omit<FetchOptions, 'body' | 'method'>,
    ) => apiFetch<T>(endpoint, { ...options, method: 'GET' }),

    post: <T>(
      endpoint: string,
      data?: unknown,
      options?: Omit<FetchOptions, 'body' | 'method'>,
    ) =>
      apiFetch<T>(endpoint, {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      }),

    put: <T>(
      endpoint: string,
      data?: unknown,
      options?: Omit<FetchOptions, 'body' | 'method'>,
    ) =>
      apiFetch<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      }),

    patch: <T>(
      endpoint: string,
      data?: unknown,
      options?: Omit<FetchOptions, 'body' | 'method'>,
    ) =>
      apiFetch<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
      }),

    delete: <T>(
      endpoint: string,
      options?: Omit<FetchOptions, 'body' | 'method'>,
    ) => apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),
  };
};
